import { useMemo, useState } from "react";
import { ArrowRight, BadgeCheck, Box, LockKeyhole, MessageCircle, Search, ShieldCheck, Sparkles, Truck } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { Product } from "@kineslima/types";
import { demoProducts } from "../data/demo";
import { PrivacyImage } from "../components/PrivacyImage";
import { ProductCard } from "../components/ProductCard";
import { api } from "../lib/api";

const categories = [
  { name: "Sets", copy: "Diseñados para combinar", image: "/images/model-black.jpg" },
  { name: "Corsets", copy: "Siluetas protagonistas", image: "/images/model-lace.jpg" },
  { name: "Edición especial", copy: "Detalles inolvidables", image: "/images/model-red.jpg" },
] as const;

const trustItems: Array<[LucideIcon,string,string]> = [
  [LockKeyhole,"Compra privada","Tus datos siempre protegidos"],
  [Box,"Empaque discreto","Sin marcas externas"],
  [Truck,"Envío rápido","Lima Metropolitana"],
  [BadgeCheck,"Calidad seleccionada","Prendas verificadas"],
];

const steps: Array<[LucideIcon,string,string]> = [
  [Search,"Encuentra tu estilo","Filtra por tipo, talla, color o presupuesto."],
  [MessageCircle,"Recibe asesoría","Escríbenos por WhatsApp con total privacidad."],
  [Truck,"Recíbelo discretamente","Coordinamos la entrega en un empaque neutro."],
];

export function HomePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const { data: home } = useQuery({ queryKey:["public-home"], queryFn:() => api<{ products:Product[] }>("/api/v1/public/home"), retry:1 });
  const products = home?.products?.length ? home.products : demoProducts;
  const suggestions = useMemo(() => query.length < 2 ? [] : products.filter((p) => `${p.name} ${p.category} ${p.colors.join(" ")}`.toLowerCase().includes(query.toLowerCase())).slice(0, 4), [products,query]);
  const submit = () => navigate(`/catalogo?q=${encodeURIComponent(query)}`);

  return (
    <>
      <section className="hero-section">
        <div className="hero-glow hero-glow-one" /><div className="hero-glow hero-glow-two" />
        <div className="mx-auto grid min-h-[780px] max-w-[1440px] items-center gap-12 px-5 pb-20 pt-32 lg:grid-cols-[.9fr_1.1fr] lg:px-8 lg:pt-24">
          <div className="relative z-10 max-w-[640px]">
            <div className="eyebrow"><Sparkles size={15} /> Nueva colección · Nocturne 2026</div>
            <h1 className="hero-title">Lencería que se siente tan bien como <em>se ve.</em></h1>
            <p className="hero-subtitle">Descubre piezas elegantes, tallas pensadas para cuerpos reales y una atención completamente privada.</p>
            <div className="hero-search-wrap">
              <Search className="text-white/40" size={21} />
              <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Buscar set, corset, color o talla..." aria-label="Buscar prendas" />
              <button onClick={submit}>Buscar</button>
              {suggestions.length > 0 && (
                <div className="search-suggestions">
                  {suggestions.map((product) => <Link key={product.id} to={`/producto/${product.slug}`}><span>{product.name}</span><small>{product.category} · S/ {product.price}</small></Link>)}
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/55">
              <span className="inline-flex items-center gap-2"><LockKeyhole size={15} className="text-fuchsia-400" /> Empaque discreto</span>
              <span className="inline-flex items-center gap-2"><Truck size={15} className="text-fuchsia-400" /> Entrega en Lima</span>
              <span className="inline-flex items-center gap-2"><MessageCircle size={15} className="text-fuchsia-400" /> Asesoría privada</span>
            </div>
          </div>

          <div className="hero-collage" aria-label="Colección destacada con identidad protegida">
            <div className="hero-card hero-card-main"><PrivacyImage src="/images/model-red.jpg" alt="Modelo adulta con conjunto floral" priority /></div>
            <div className="hero-card hero-card-top"><PrivacyImage src="/images/model-lace.jpg" alt="Modelo adulta con corset negro" priority showBadge={false} /></div>
            <div className="hero-card hero-card-bottom"><PrivacyImage src="/images/model-black.jpg" alt="Modelo adulta con set negro" priority showBadge={false} /></div>
            <div className="hero-note"><ShieldCheck size={18} /><span><strong>Privacidad visual</strong><small>Rostros protegidos</small></span></div>
          </div>
        </div>
      </section>

      <section className="trust-strip">
        <div className="mx-auto grid max-w-[1380px] grid-cols-2 gap-6 px-5 py-7 md:grid-cols-4 lg:px-8">
          {trustItems.map(([Icon,title,copy]) => (
            <div key={String(title)} className="trust-item"><Icon size={21} /><span><strong>{String(title)}</strong><small>{String(copy)}</small></span></div>
          ))}
        </div>
      </section>

      <section className="section-wrap">
        <div className="section-heading"><div><span className="section-kicker">Explora tu estilo</span><h2>Colecciones para cada versión de ti</h2></div><Link to="/catalogo">Ver todo <ArrowRight size={17} /></Link></div>
        <div className="category-grid">
          {categories.map((item) => (
            <Link key={item.name} to={`/catalogo?categoria=${encodeURIComponent(item.name)}`} className="category-card">
              <PrivacyImage src={item.image} alt={`Colección ${item.name}`} showBadge={false} />
              <div><p>{item.copy}</p><h3>{item.name}</h3><span>Descubrir <ArrowRight size={15} /></span></div>
            </Link>
          ))}
        </div>
      </section>

      <section className="section-wrap pt-2">
        <div className="section-heading"><div><span className="section-kicker">Los favoritos</span><h2>Piezas que están enamorando</h2></div><Link to="/catalogo">Ver catálogo <ArrowRight size={17} /></Link></div>
        <div className="product-grid">{products.filter((p) => p.status === "ACTIVE").slice(0, 4).map((p) => <ProductCard key={p.id} product={p} />)}</div>
      </section>

      <section className="section-wrap py-10">
        <div className="editorial-banner">
          <PrivacyImage src="/images/model-black.jpg" alt="Colección Nocturne" showBadge={false} />
          <div className="editorial-overlay" />
          <div className="editorial-copy"><span>Edición limitada</span><h2>Nocturne</h2><p>Encaje, sombras y detalles que hablan en voz baja.</p><Link to="/catalogo">Ver colección <ArrowRight size={17} /></Link></div>
        </div>
      </section>

      <section className="section-wrap text-center">
        <span className="section-kicker">Así de simple</span><h2 className="mx-auto mt-2 max-w-xl font-display text-3xl font-bold tracking-tight md:text-4xl">Una experiencia cómoda, privada y sin complicaciones</h2>
        <div className="steps-grid">
          {steps.map(([Icon,title,copy], i) => (
            <div className="step-card" key={String(title)}><span className="step-number">0{i+1}</span><span className="step-icon"><Icon /></span><h3>{String(title)}</h3><p>{String(copy)}</p></div>
          ))}
        </div>
      </section>

      <section className="section-wrap pb-24">
        <div className="cta-panel"><div><span className="section-kicker">¿No sabes qué elegir?</span><h2>Te ayudamos a encontrar tu talla y estilo ideal.</h2><p>Atención personal, respetuosa y completamente privada.</p></div><Link className="primary-button" to="/contacto"><MessageCircle size={18} /> Hablar con una asesora</Link></div>
      </section>
    </>
  );
}
